import React, { useEffect } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  useGetBlogDetailsQuery,
  useGetBlogCategoriesQuery,
} from '../redux/apiSlice';
import {
  ArrowLeft,
  Share2,
  AlertTriangle,
  Calendar,
  Tag,
  User,
} from 'lucide-react';

const BlogDetails: React.FC = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { data, isLoading, error } = useGetBlogDetailsQuery(slug || '');
  const { data: categoriesData } = useGetBlogCategoriesQuery();

  // Get the first blog from the array response (if it's an array) or use directly
  const blog = Array.isArray(data) ? data[0] : data;

  // Use real categories from API or fallback to static ones
  const categories = categoriesData || [
    { id: 1, name: 'Programming', slug: 'programming', postCount: 12 },
    { id: 2, name: 'Web Development', slug: 'web-development', postCount: 8 },
    { id: 3, name: 'Mobile Apps', slug: 'mobile-apps', postCount: 5 },
    { id: 4, name: 'AI & Machine Learning', slug: 'ai-ml', postCount: 7 },
    { id: 5, name: 'Game Development', slug: 'game-dev', postCount: 4 },
    { id: 6, name: 'Tips & Tricks', slug: 'tips-tricks', postCount: 15 },
  ];

  // Set SEO meta tags
  useEffect(() => {
    if (blog) {
      document.title = blog.seoTitle || blog.title;

      // Update meta description
      const metaDescription = document.querySelector(
        'meta[name="description"]',
      );
      if (metaDescription) {
        metaDescription.setAttribute(
          'content',
          blog.seoDescription || blog.excerpt,
        );
      } else {
        const newMetaDescription = document.createElement('meta');
        newMetaDescription.name = 'description';
        newMetaDescription.content = blog.seoDescription || blog.excerpt;
        document.head.appendChild(newMetaDescription);
      }
    }

    // Cleanup function to reset title when component unmounts
    return () => {
      document.title = 'MyCodeDragon';
    };
  }, [blog]);

  // Function to get default image if featuredImage is not available
  const getImageSrc = (featuredImage: string) => {
    if (!featuredImage || featuredImage === '') {
      return '/assets/images/final_logo.png'; // Default image
    }
    // Check if it's a full URL or relative path
    if (featuredImage.startsWith('http')) {
      return featuredImage;
    }
    // Assume it's from the API server
    return `${process.env.REACT_APP_API_URL}${featuredImage}`;
  };

  // Function to format date
  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  // Function to get category names and slugs - categories are already objects in the blog data
  const getCategoryNames = (blogCategories: any[]) => {
    // If categories are already objects with name property, return them directly
    if (
      blogCategories &&
      blogCategories.length > 0 &&
      typeof blogCategories[0] === 'object'
    ) {
      return blogCategories.map((cat) => ({ name: cat.name, slug: cat.slug }));
    }
    // Fallback: if categories are IDs, map them to names
    return categories
      .filter((cat) => blogCategories.includes(cat.id))
      .map((cat) => ({ name: cat.name, slug: cat.slug }));
  };

  const handleShare = async () => {
    if (navigator.share && blog) {
      try {
        await navigator.share({
          title: blog.title,
          text: blog.excerpt,
          url: window.location.href,
        });
      } catch (error) {
        // Fallback to copying URL
        navigator.clipboard.writeText(window.location.href);
        alert('Link copied to clipboard!');
      }
    } else {
      // Fallback for browsers without Web Share API
      navigator.clipboard.writeText(window.location.href);
      alert('Link copied to clipboard!');
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-blue-50 to-pink-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          <div className="animate-pulse">
            {/* Header skeleton */}
            <div className="flex items-center mb-8">
              <div className="w-8 h-8 bg-gray-300 rounded-full mr-4"></div>
              <div className="h-4 bg-gray-300 rounded w-24"></div>
            </div>

            {/* Title skeleton */}
            <div className="h-8 bg-gray-300 rounded w-3/4 mb-4"></div>
            <div className="h-6 bg-gray-300 rounded w-1/2 mb-8"></div>

            {/* Image skeleton */}
            <div className="h-64 bg-gray-300 rounded-2xl mb-8"></div>

            {/* Content skeleton */}
            <div className="space-y-4">
              <div className="h-4 bg-gray-300 rounded w-full"></div>
              <div className="h-4 bg-gray-300 rounded w-5/6"></div>
              <div className="h-4 bg-gray-300 rounded w-4/6"></div>
              <div className="h-4 bg-gray-300 rounded w-full"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error || !blog) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-blue-50 to-pink-50 flex items-center justify-center">
        <div className="text-center bg-white rounded-2xl shadow-lg p-8 max-w-md">
          <div className="text-red-500 mb-4">
            <AlertTriangle className="w-16 h-16 mx-auto" />
          </div>
          <h3 className="text-xl font-bold text-gray-800 mb-2">
            Story Not Found
          </h3>
          <p className="text-gray-600 mb-4">
            This coding adventure might have moved to a different place!
          </p>
          <Link
            to="/blog"
            className="bg-[#004aad] hover:bg-blue-800 text-white px-6 py-2 rounded-full font-medium transition-colors duration-200 inline-block"
          >
            Back to Blogs
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-blue-50 to-pink-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Navigation Header */}
        <div className="flex items-center justify-between mb-8">
          <button
            onClick={() => navigate(-1)}
            className="flex items-center text-[#004aad] hover:text-blue-800 font-medium transition-colors duration-200 group"
          >
            <ArrowLeft className="w-5 h-5 mr-2 group-hover:-translate-x-1 transition-transform duration-200" />
            Back
          </button>

          <button
            onClick={handleShare}
            className="flex items-center space-x-2 px-4 py-2 rounded-full bg-gray-100 text-gray-600 hover:bg-blue-100 hover:text-blue-800 transition-all duration-200"
          >
            <Share2 className="w-5 h-5" />
            <span className="font-medium">Share</span>
          </button>
        </div>

        {/* Blog Content */}
        <article className="bg-white rounded-3xl shadow-lg overflow-hidden">
          {/* Blog Header */}
          <div className="p-8 pb-6">
            {/* Title */}
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4 leading-tight">
              {blog.title}
            </h1>

            {/* Meta Information */}
            <div className="flex flex-wrap items-center gap-4 mb-6 text-sm text-gray-600">
              <div className="flex items-center">
                <Calendar className="w-4 h-4 mr-2" />
                {formatDate(blog.publishedAt)}
              </div>

              <div className="flex items-center">
                <User className="w-4 h-4 mr-2" />
                By {blog.author.name}
              </div>

              {/* Categories */}
              {blog.categories.length > 0 && (
                <div className="flex items-center gap-2">
                  {getCategoryNames(blog.categories).map((category, index) => (
                    <Link
                      key={index}
                      to={`/blog/category/${category.slug}`}
                      className="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-blue-100 text-[#004aad] hover:bg-blue-200 transition-colors duration-200"
                    >
                      <Tag className="w-3 h-3 mr-1" />
                      {category.name}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {/* Action Buttons */}
            <div className="flex items-center space-x-4 mb-6"></div>
          </div>

          {/* Blog Image */}
          {blog.featuredImage && (
            <div className="relative h-64 sm:h-80 lg:h-96 overflow-hidden mx-8 rounded-2xl mb-8">
              <img
                src={getImageSrc(blog.featuredImage)}
                alt={blog.alttext || blog.title}
                className="w-full h-full object-cover"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.src = '/assets/images/final_logo.png';
                }}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/10 to-transparent"></div>
            </div>
          )}

          {/* Blog Article Content */}
          <div className="px-8 pb-8">
            <style>
              {`
                .blog-content ul {
                  list-style-type: disc !important;
                  margin-left: 1.5rem !important;
                  padding-left: 0 !important;
                }
                .blog-content ol {
                  list-style-type: decimal !important;
                  margin-left: 1.5rem !important;
                  padding-left: 0 !important;
                }
                .blog-content li {
                  display: list-item !important;
                  list-style-position: outside !important;
                  margin-bottom: 0.5rem !important;
                }
                .blog-content ul li::marker {
                  color: #6b7280 !important;
                }
                .blog-content ol li::marker {
                  color: #6b7280 !important;
                }
              `}
            </style>
            <div
              className="prose prose-lg max-w-none blog-content
                         prose-headings:text-gray-900 prose-headings:font-bold
                         prose-p:text-gray-700 prose-p:leading-relaxed prose-p:text-base
                         prose-a:text-purple-600 prose-a:no-underline hover:prose-a:text-purple-700 hover:prose-a:underline
                         prose-strong:text-gray-900 prose-strong:font-semibold
                         prose-ul:text-gray-700 prose-ol:text-gray-700
                         prose-li:text-gray-700 prose-li:leading-relaxed prose-li:list-disc prose-li:ml-6
                         prose-ul:list-disc prose-ul:ml-6 prose-ul:space-y-2
                         prose-ol:list-decimal prose-ol:ml-6 prose-ol:space-y-2
                         prose-blockquote:border-purple-200 prose-blockquote:bg-purple-50 prose-blockquote:p-4 prose-blockquote:rounded-lg
                         prose-code:bg-purple-100 prose-code:text-purple-800 prose-code:px-2 prose-code:py-1 prose-code:rounded
                         prose-pre:bg-gray-900 prose-pre:text-gray-100 prose-pre:rounded-lg
                         prose-img:rounded-lg prose-img:shadow-md
                         children-friendly-content"
              dangerouslySetInnerHTML={{ __html: blog.content }}
            />
          </div>
        </article>

        {/* Fun Call-to-Action */}
        <div className="mt-12 bg-gradient-to-r from-purple-600 to-pink-600 rounded-3xl p-8 text-center text-white">
          <h3 className="text-2xl font-bold mb-4">
            🎉 Ready for More Coding Adventures?
          </h3>
          <p className="text-lg mb-6 opacity-90">
            Discover more exciting blogs and learn amazing coding skills!
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/blog"
              className="bg-white text-purple-600 px-6 py-3 rounded-full font-semibold hover:bg-gray-100 transition-colors duration-200"
            >
              Read More Blogs
            </Link>
            <Link
              to="/courses"
              className="bg-purple-800 text-white px-6 py-3 rounded-full font-semibold hover:bg-purple-900 transition-colors duration-200"
            >
              Start Learning to Code
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BlogDetails;
